import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as bookingApi from "./bookingApi";
import { DeskHeader, DeskPicker, MeetingSide, slotLine, useWide } from "./desktop";
import { loadApollo } from "./apollo";
import { reportHeight, showInParent, tellParent } from "./embed";
import { browserZone, clock, dayKey, longDay, zoneCity } from "./format";
import {
  Divider,
  Field,
  Header,
  Mono,
  Page,
  PreviewBar,
  PrimaryButton,
  Skeleton,
  SlotPicker,
  TextLink,
  TimeGrid,
  ZonePicker,
} from "./parts";
import WhereStep, { placeLine } from "./WhereStep";

/*
 * /book — BOOK FIRST. Ruled 2026-09-29: "get the meeting booked FIRST and
 * foremost, and then we can mess with the agenda." A time, a name, an email,
 * and nothing else stands between the lead and the calendar. What the call is
 * for is asked on the success screen (BookedPage).
 *
 * States, from the concept decks: S1 loading, S2 no times, S4 calendar down,
 * S8 time just taken (only the time needs picking again). Plus "not open",
 * which no deck drew because a lead should never see it.
 */

const ZONE_KEY = "bk_zone";

function initialZone() {
  try {
    return localStorage.getItem(ZONE_KEY) || browserZone();
  } catch {
    return browserZone();
  }
}

/** The three open times nearest to the one that was just taken, same day first. */
function nearest(slots, takenStart, zone) {
  const at = new Date(takenStart).getTime();
  const day = dayKey(takenStart, zone);
  // Never offer the taken time back, even when the calendar has not caught up
  // with the booking that took it.
  const open = slots.filter((s) => new Date(s.start).getTime() !== at);
  const sameDay = open.filter((s) => dayKey(s.start, zone) === day);
  const pool = sameDay.length ? sameDay : open;
  return [...pool].sort((a, b) => Math.abs(new Date(a.start) - at) - Math.abs(new Date(b.start) - at)).slice(0, 2);
}

/** In person, the Where line sits over the desktop picker; otherwise nothing wraps it. */
function PickerWrap({ where, children }) {
  return where ? (
    <div className="bkd-picker-wrap">
      {where}
      {children}
    </div>
  ) : (
    children
  );
}

export default function BookPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // A meeting type's private link, /book/<slug>. None is the lead intro call.
  const { slug = null } = useParams();
  const invite = query.get("c");
  // Sent here by a landing page's form: the token that names the submission.
  const handoff = query.get("lp");
  // The CRM's Page-tab preview: a pass, and whether to greet the sample lead.
  const preview = query.get("p");
  const previewAs = query.get("as");
  // Where they came from, for the visit count: read once, since the page's own
  // navigation (a cancelled call's "book a new time") would otherwise name us.
  const [source] = useState(() => ({
    ref: typeof document !== "undefined" ? document.referrer || null : null,
    utm: query.get("utm_source"),
  }));
  // "Book a new time" from a cancelled call carries its agenda and note here.
  const carried = (location.state && location.state.carry) || null;

  const [pickedZone, setZone] = useState(initialZone);
  /*
   * In person, where comes first (WhereStep). ?at=office or ?at=theirs is his
   * choice made in the link; without it the page asks.
   */
  const forcedAt = ["office", "theirs"].includes(query.get("at")) ? query.get("at") : null;
  const [place, setPlace] = useState(forcedAt === "office" ? { at: "office" } : null);
  const [lastPlace, setLastPlace] = useState(null);
  const [placeError, setPlaceError] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [name, setName] = useState((carried && carried.name) || "");
  const [email, setEmail] = useState((carried && carried.email) || "");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [taken, setTaken] = useState(null); // S8: the start someone else grabbed
  const [showAll, setShowAll] = useState(false);
  const wide = useWide();

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await bookingApi.getSlots({ tz: pickedZone, invite, handoff, preview, as: previewAs, type: slug, place, ...source });
      setData(page);
      if (page.prefill) {
        setName((v) => v || page.prefill.name || "");
        setEmail((v) => v || page.prefill.email || "");
      }
      return page;
    } catch (err) {
      // An address we could not use goes back to the where step, said there.
      if (place && err.status === 400) {
        setPlaceError(err.message);
        setPlace(null);
        return null;
      }
      setError(err);
      return null;
    }
  }, [pickedZone, invite, handoff, preview, previewAs, slug, source, place]);

  useEffect(() => {
    load();
  }, [load]);

  // Apollo names the visitor's company; not for the CRM's own preview.
  useEffect(() => loadApollo({ preview }), [preview]);

  // The CRM's live list: this visit beats while the page is open.
  const beat = data ? data.beat : null;
  useEffect(() => bookingApi.startBeat(beat), [beat]);

  // Inside a landing page's form card: say we're here and how tall we are.
  useEffect(() => {
    tellParent("ready");
    reportHeight();
  }, []);

  /*
   * In the card the frame never scrolls, the landing page does: a picked time
   * opens the form below the grid, often under the fold, so ask for it.
   */
  const formRef = useRef(null);
  useEffect(() => {
    if (slot) showInParent(formRef.current);
  }, [slot]);

  /*
   * The landing page does not wait for its form to reach the server before
   * sending them here, so the first read can beat it. One more look shortly
   * after fills the name and email in; the booking links up either way.
   */
  const retried = useRef(false);
  useEffect(() => {
    if (!handoff || !data || data.prefill || retried.current) return undefined;
    retried.current = true;
    const t = setTimeout(load, 1500);
    return () => clearTimeout(t);
  }, [handoff, data, load]);

  const inPerson = !!(data && data.type && data.type.in_person);
  // In person the times are in the room's clock, not the browser's.
  const zone = inPerson && data.timezone ? data.timezone : pickedZone;

  const choosePlace = (next) => {
    setPlaceError(null);
    setLastPlace(next);
    setSlot(null);
    setDay(null);
    setPlace(next);
  };

  const changeZone = (next) => {
    try {
      localStorage.setItem(ZONE_KEY, next);
    } catch {
      /* a private window: the zone just is not remembered */
    }
    setSlot(null);
    setDay(null);
    setData(null);
    setZone(next);
  };

  const submit = async () => {
    if (!slot) return;
    setFormError(null);
    if (!name.trim()) return setFormError("Please add your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setFormError("Please add a valid work email.");
    setBusy(true);
    if (data && data.preview) {
      // Preview: no booking, straight to the sample success screen for this time.
      try {
        const sample = await bookingApi.getPreviewSuccess({ preview, tz: zone, as: previewAs, type: slug });
        const params = new URLSearchParams({
          p: preview,
          tz: zone,
          ...(previewAs ? { as: previewAs } : {}),
          ...(slug ? { type: slug } : {}),
        });
        navigate(`/book/booked/preview?${params}`, {
          state: { booking: { ...sample, start: slot.start, end: slot.end, name, email, ...(inPerson ? { location: data.location, meet_url: null, timezone: zone } : {}) } },
        });
      } catch (err) {
        setFormError(err.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const result = await bookingApi.book({
        start: slot.start,
        tz: zone,
        name,
        email,
        c: invite,
        lp: handoff,
        type: slug,
        place,
        website,
        ...(carried && carried.intent ? { intent: carried.intent } : {}),
        ...(carried && carried.note ? { note: carried.note } : {}),
      });
      tellParent("booked");
      navigate(`/book/booked/${result.token}`, { replace: true, state: { booking: result, fresh: true } });
    } catch (err) {
      if (err.code === "slot_taken") {
        setTaken(slot.start);
        setShowAll(false);
        const page = await load();
        const next = page ? nearest(page.slots, slot.start, zone)[0] : null;
        setSlot(next || null);
        if (next) setDay(dayKey(next.start, zone));
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (err.status === 503 || err.status >= 500) {
        setError(err);
      } else {
        setFormError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const personal = data && data.prefill;
  const company = personal && personal.company;
  const firstName = personal && personal.name ? personal.name.split(" ")[0] : null;
  const minutes = (data && data.meeting_minutes) || 30;
  const hostEmail = (data && data.host_email) || (error && error.hostEmail) || null;
  const host = (data && data.host) || "Gabriel";
  const type = data && data.type;
  // "INTRO CALL", "FOLLOW-UP CALL" or the meeting type's own name, in the page's mono labels.
  const kind = type && (!type.lead || type.follow_up) ? String(type.name).toUpperCase() : "INTRO CALL";

  const headerProps = company
    ? { kicker: `${company} × Remotion`.toUpperCase(), right: `${minutes} MIN` }
    : { logo: true, right: `${kind} · ${minutes} MIN` };

  /* ---------- states that replace the page ---------- */

  if (error && (error.status === 503 || error.status >= 500)) {
    return (
      <Page bar={`${kind} · ${minutes} MIN`}>
        <div className="bk-body bk-body--top">
          <Mono className="bk-alert-kicker">● Booking is down for a moment</Mono>
          <h1 className="bk-title">Hey. Our calendar hiccuped.</h1>
          <p className="bk-lede">
            Nothing you typed is lost. Try again in a minute. If it's still stuck, email me and I'll book it by hand.
          </p>
          {slot ? (
            <div className="bk-card bk-card--quiet">
              <p>Holding: {longDay(slot.start, zone)} · {clock(slot.start, zone)}</p>
              {name || email ? <p>{[name, email].filter(Boolean).join(" · ")}</p> : null}
            </div>
          ) : null}
          <PrimaryButton onClick={() => (slot ? (setError(null), submit()) : load())}>Try again</PrimaryButton>
          {hostEmail ? (
            <a className="bk-link bk-link--block" href={`mailto:${hostEmail}?subject=${encodeURIComponent("Booking a call")}`}>
              Email {host} instead
            </a>
          ) : null}
        </div>
      </Page>
    );
  }

  if (error) {
    const paused = error.status === 404;
    return (
      <Page bar={`${kind} · ${minutes} MIN`}>
        <div className="bk-body bk-body--top bk-split">
          <div className="bk-split__main">
            {paused ? <Mono className="bk-muted">Booking is paused</Mono> : null}
            <h1 className="bk-title">{paused ? "Hey. I've paused new bookings for a bit." : "Something went wrong."}</h1>
            <p className="bk-lede">{paused ? "Email me and I'll find us a time directly." : error.message}</p>
            {paused && hostEmail ? (
              <a className="bk-primary bk-primary--link bk-primary--fit" href={`mailto:${hostEmail}?subject=${encodeURIComponent("Finding a time")}`}>
                Email {host}
              </a>
            ) : null}
          </div>
          {paused ? (
            <div className="bk-card bk-card--quiet bk-split__side">
              <Mono>Already booked?</Mono>
              <p className="bk-strong">Your call still stands.</p>
              <p>Use the link in your Google invite to see it, move it or cancel it.</p>
            </div>
          ) : null}
        </div>
      </Page>
    );
  }

  /* ---------- in person: where, before when ---------- */

  if (inPerson && data.needs_place) {
    const headline = (type && type.headline) || `Hey. Let's meet in person.`;
    const step = (
      <WhereStep
        type={type}
        host={host}
        forced={forcedAt}
        initial={lastPlace}
        busy={!!place}
        error={placeError}
        onSubmit={choosePlace}
      />
    );
    return (
      <Page>
        {data.preview ? <PreviewBar /> : null}
        {wide ? (
          <DeskHeader right={`${kind} · ${minutes} MIN`}>{headline}</DeskHeader>
        ) : (
          <Header logo right={`${kind} · ${minutes} MIN`}>
            {headline}
          </Header>
        )}
        <div className={wide ? "bkd-body bkd-cols" : "bk-body"}>
          {wide ? <section className="bkd-picker bkd-picker--where">{step}</section> : step}
          {wide ? <MeetingSide host={host} title={data.host_title} photo={data.host_photo} minutes={minutes} where="wherever suits you" /> : null}
        </div>
      </Page>
    );
  }

  const whereLine =
    inPerson && data.location ? (
      <div className="bk-row bk-where__line">
        <span>
          <Mono>Where</Mono> {placeLine(data.location, host)}
        </span>
        {forcedAt !== "office" ? <TextLink onClick={() => choosePlace(null)}>Change</TextLink> : null}
      </div>
    ) : null;
  const fine =
    inPerson && data && data.location
      ? `In person, ${placeLine(data.location, host)}. The invite comes from Google Calendar.`
      : "Google Meet. The invite comes from Google Calendar.";

  if (data && !data.slots.length) {
    return (
      <Page bar={`${kind} · ${minutes} MIN`}>
        <div className="bk-body bk-body--top bk-full">
          <div className="bk-target" aria-hidden="true" />
          <h1 className="bk-title">Hey. My calendar's full for the next two weeks.</h1>
          <p className="bk-lede">
            That's on me, not you. Email me and I'll find a time that works, even if it's outside my usual hours.
          </p>
          {hostEmail ? (
            <a className="bk-primary bk-primary--link" href={`mailto:${hostEmail}?subject=${encodeURIComponent("Finding a time")}`}>
              Email {host}
            </a>
          ) : null}
          <TextLink onClick={load}>Check again later</TextLink>
        </div>
      </Page>
    );
  }

  /* ---------- the page ---------- */

  const headline = company
    ? `Hey ${firstName || "there"}. Let's talk about LinkedIn Ads. Pick a time.`
    : type && !type.lead && type.headline
    ? type.headline
    : `Hey. ${minutes} minutes with ${host}. Pick a time.`;

  /*
   * DESKTOP (Remotion_Booking_-_Desktop.pptx, 1A-1D, 5A, 5B): days beside
   * times, the form opening right under the clicked time's group, and the
   * side column. Same state and the same submit as the phone layout.
   */
  if (wide) {
    const form = (s) => (
      <form
        className="bkd-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {taken ? (
          <div className="bk-banner bk-banner--red" role="alert">
            <strong>Someone just grabbed {clock(taken, zone)}. Sorry.</strong>
            <span>Your details are saved. Pick another time and you're done.</span>
          </div>
        ) : null}
        <span className="bk-mono">{slotLine(s, zone)}</span>
        <div className="bkd-form__fields">
          <Field type="text" name="name" autoComplete="name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          <Field
            type="email"
            name="email"
            autoComplete="email"
            placeholder="Work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
          />
        </div>
        <input className="bk-hp" type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} aria-hidden="true" />
        {formError ? <p className="bk-error">{formError}</p> : null}
        <PrimaryButton type="submit" busy={busy} className="bk-primary bk-primary--fit">
          Book {longDay(s.start, zone)} · {clock(s.start, zone)}
        </PrimaryButton>
        <p className="bk-muted">{fine}</p>
      </form>
    );
    return (
      <Page>
        {data && data.preview ? <PreviewBar /> : null}
        <DeskHeader right={`${kind} · ${minutes} MIN`} kicker={company ? `${company} × Remotion`.toUpperCase() : null}>
          {headline}
        </DeskHeader>
        <div className="bkd-body bkd-cols">
          {data ? (
            <PickerWrap where={whereLine}>
            <DeskPicker
              zone={zone}
              onZone={changeZone}
              slots={data.slots}
              days={data.days}
              day={day}
              onDay={(k) => {
                setDay(k);
                setSlot(null);
              }}
              selected={slot && slot.start}
              taken={taken}
              minutes={minutes}
              onSelect={(s) => {
                setSlot(s);
                setDay(dayKey(s.start, zone));
              }}
              panel={form}
              where={inPerson ? "In person" : "Google Meet"}
              fixedZone={inPerson}
            />
            </PickerWrap>
          ) : (
            <section className="bkd-picker bkd-picker--loading">
              <div className="bkd-picker__top">
                <span className="bk-mono">When</span>
                <span className="bk-muted">Finding open times…</span>
              </div>
              <Skeleton />
            </section>
          )}
          <MeetingSide
            host={host}
            title={data && data.host_title}
            photo={data && data.host_photo}
            minutes={minutes}
            agendas={data && data.agendas}
            where={inPerson && data.location ? placeLine(data.location, host) : null}
          />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      {data && data.preview ? <PreviewBar /> : null}
      {taken ? (
        <Header {...headerProps} />
      ) : (
        <Header {...headerProps}>{headline}</Header>
      )}

      <div className="bk-body">
        {taken ? (
          <div className="bk-banner bk-banner--red" role="alert">
            <strong>Someone just grabbed {clock(taken, zone)}. Sorry.</strong>
            <span>Your details are saved. Pick another time.</span>
          </div>
        ) : null}

        {!data ? (
          <>
            <div className="bk-row">
              <Mono>When</Mono>
              <span className="bk-muted">Finding open times…</span>
            </div>
            <Skeleton />
          </>
        ) : taken && !showAll ? (
          <>
            <Mono className="bk-section">Nearest open times · {longDay(taken, zone)}</Mono>
            <TimeGrid
              slots={[{ start: taken }, ...nearest(data.slots, taken, zone)].sort(
                (a, b) => new Date(a.start) - new Date(b.start)
              )}
              zone={zone}
              selected={slot && slot.start}
              disabledStart={taken}
              onSelect={(s) => setSlot(data.slots.find((x) => x.start === s.start) || null)}
            />
            <TextLink onClick={() => setShowAll(true)}>See all times</TextLink>
          </>
        ) : (
          <>
            {whereLine}
            <div className="bk-row">
              <Mono>When</Mono>
              {inPerson ? <span className="bk-muted">{zoneCity(zone)} time</span> : <ZonePicker zone={zone} onChange={changeZone} />}
            </div>
            <SlotPicker
              slots={data.slots}
              zone={zone}
              day={day}
              onDay={setDay}
              selected={slot && slot.start}
              disabledStart={taken}
              onSelect={(s) => {
                setSlot(s);
                setDay(dayKey(s.start, zone));
              }}
            />
          </>
        )}

        <Divider />

        {slot ? (
          <form
            ref={formRef}
            className="bk-form"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Field
              type="text"
              name="name"
              autoComplete="name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
            <Field
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              placeholder="Work email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
            />
            {/* Honeypot: hidden from people, irresistible to form-filling bots. */}
            <input
              className="bk-hp"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              aria-hidden="true"
            />
            {formError ? <p className="bk-error">{formError}</p> : null}
            <PrimaryButton type="submit" busy={busy}>
              Book {longDay(slot.start, zone)} · {clock(slot.start, zone)}
            </PrimaryButton>
            <p className="bk-fine">{fine}</p>
          </form>
        ) : data ? (
          <p className="bk-lede">{minutes} minutes. Once it's booked, you can choose what we cover.</p>
        ) : null}
      </div>
    </Page>
  );
}
